import { useQuery } from '@tanstack/react-query';
import { maitriApi } from '../api/modules';
import { Drawer, DrawerCloseButton } from '../components/ui/Drawer';
import { StatusPill } from '../components/ui/StatusPill';
import { useToast } from '../components/toast/Toast';
import { relativeTime, timeOfDay } from '../lib/format';

interface Props {
  open: boolean;
  code: string | null;
  onClose: () => void;
}

export function MaitriWorkerDrawer({ open, code, onClose }: Props) {
  const { show } = useToast();
  const { data, isLoading } = useQuery({
    enabled: !!code,
    queryKey: ['maitri-worker', code],
    queryFn: () => maitriApi.detail(code!),
  });

  return (
    <Drawer open={open && !!code} onClose={onClose}>
      <div className="bg-navy-800 text-white px-6 py-5 flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest opacity-80">MAITRI Worker · {code}</div>
          <h2 className="serif-display text-2xl font-medium mt-1">{data ? data.name : 'Loading…'}</h2>
          {data && (
            <div className="text-sm opacity-90 mt-2">
              {data.location.gp ?? data.location.district} · {data.location.district}
            </div>
          )}
        </div>
        <DrawerCloseButton onClick={onClose} />
      </div>

      <div className="px-6 py-5 flex-1 overflow-y-auto space-y-5">
        {isLoading || !data ? (
          <div className="space-y-3">
            <div className="h-20 bg-cream-100 rounded-sm animate-pulse" />
            <div className="h-40 bg-cream-100 rounded-sm animate-pulse" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-cream-50 p-3 rounded-sm">
                <div className="text-[10px] uppercase tracking-widest text-navy-800/50">Status</div>
                <div className="mt-1"><StatusPill status={data.active ? 'active' : 'inactive'} /></div>
              </div>
              <div className="bg-cream-50 p-3 rounded-sm">
                <div className="text-[10px] uppercase tracking-widest text-navy-800/50">Mobile</div>
                <div className="font-numeric text-sm text-navy-800 mt-1">{data.mobile ?? '—'}</div>
              </div>
              <div className="bg-cream-50 p-3 rounded-sm">
                <div className="text-[10px] uppercase tracking-widest text-navy-800/50">Training batch</div>
                <div className="text-sm font-medium text-navy-800 mt-1">{data.training_batch ?? '—'}</div>
                <div className="text-[11px] text-navy-800/60 mt-0.5 font-numeric">
                  {data.certified_at ? `Certified ${new Date(data.certified_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}` : 'Not yet certified'}
                </div>
              </div>
              <div className="bg-cream-50 p-3 rounded-sm">
                <div className="text-[10px] uppercase tracking-widest text-navy-800/50">Bharat Pashudhan</div>
                <div className="font-numeric text-sm text-navy-800 mt-1">{data.bharat_pashudhan_id ?? '—'}</div>
              </div>
            </div>

            {/* Services by type */}
            <div>
              <h3 className="text-xs uppercase tracking-widest text-navy-800/50 mb-2 font-semibold">
                Services (last 30 days)
              </h3>
              {Object.keys(data.services_by_type).length === 0 ? (
                <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-sm text-sm">
                  <div className="font-medium">No services in 30 days</div>
                  <div className="text-xs mt-1 text-red-600">
                    {data.last_service_at
                      ? `Last service: ${relativeTime(data.last_service_at)}`
                      : 'Worker has never logged a service. Action: AI follow-up recommended.'}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(data.services_by_type).map(([type, count]) => (
                    <div key={type} className="bg-cream-50 p-3 rounded-sm">
                      <div className="text-[10px] uppercase tracking-wider text-navy-800/50">{type.replace(/_/g, ' ')}</div>
                      <div className="font-numeric serif-display text-xl text-navy-800 font-medium mt-1">{count}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recent services log */}
            <div>
              <h3 className="text-xs uppercase tracking-widest text-navy-800/50 mb-2 font-semibold">Recent service log</h3>
              {data.recent_services.length === 0 ? (
                <div className="text-xs text-navy-800/50 bg-cream-50 p-3 rounded-sm text-center">No service log entries</div>
              ) : (
                <div className="border border-cream-200 rounded-sm overflow-hidden text-xs">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-cream-100">
                        <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-navy-800/70 font-semibold">Type</th>
                        <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-navy-800/70 font-semibold">Beneficiary</th>
                        <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-navy-800/70 font-semibold">When</th>
                        <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider text-navy-800/70 font-semibold">Sync</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recent_services.slice(0, 12).map((s, i) => (
                        <tr key={i} className="border-t border-cream-100">
                          <td className="px-3 py-2 font-medium text-navy-800 capitalize">{s.type.replace(/_/g, ' ')}</td>
                          <td className="px-3 py-2 text-navy-800/80">{s.beneficiary_name ?? '—'}</td>
                          <td className="px-3 py-2 font-numeric text-navy-800/70">{timeOfDay(s.performed_at)}</td>
                          <td className="px-3 py-2 text-center">
                            {s.synced ? (
                              <span className="inline-flex w-4 h-4 rounded-full bg-green-100 text-green-700 items-center justify-center">
                                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                              </span>
                            ) : (
                              <span className="text-amber-600 text-[10px]">queued</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className="border-t border-cream-200 px-6 py-4 flex items-center gap-2 bg-cream-50">
        <button
          type="button"
          onClick={() => show('Activity report queued — will be emailed')}
          className="flex-1 bg-navy-800 hover:bg-navy-700 text-white text-sm py-2.5 rounded-sm"
        >
          Export activity report
        </button>
        {data && !data.active && (
          <button
            type="button"
            onClick={() => show('Re-activation requested · Mandal AD will follow up')}
            className="px-4 py-2.5 border border-saffron-500 text-saffron-600 hover:bg-saffron-50 text-sm rounded-sm"
          >
            Request re-activation
          </button>
        )}
      </div>
    </Drawer>
  );
}
